import { AppService } from './../../../../../core/app.service';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivtieResponse } from '../../../../../data/http/responses/activite.response';
import { StructureResponse } from '../../../../../data/http/responses/strcuture.res';
import { LigneBudgetaireResponse } from '../../../../../data/http/responses/lignebudgetaire.res';
import { LoaderComponent } from '../../../../../components/ui/loader/loader.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ActiviteService } from '../../../../../services/activite/activite.service';
import { LigneBudgetaireService } from '../../../../../services/lignebudgetaire/lignebudgetaire.service';
import { ExpressionBesoinService } from '../../../../../services/expressionbesoin.service';
import { StructureService } from '../../../../../services/StructureService';
import { MatIcon } from '@angular/material/icon';
import { routeAlias } from '../../../../../../routesalias';
import { ExpressionBesoinReq } from '../../../../../data/http/requests/expression.req';
import { toCamelCase, toSnakeCaseUpper } from '../../../../../utils/helpers';
import { Input } from '../../../../../components/forms/input/input';
import { SelectOption } from '../../../../../data/schemas/selectoption.schema';
import { SelectComponent } from '../../../../../components/forms/select/select.component';
import { UiButtonComponent } from '../../../../../components/button/button.component';

@Component({
  selector: 'app-edit',
  imports: [ReactiveFormsModule, LoaderComponent, MatIcon, Input, SelectComponent, UiButtonComponent],
  templateUrl: './edit.component.html',
  styleUrl: './edit.component.css'
})
export class EditExpressionBesoinPage implements OnInit {
  expressionBesoinForm!: FormGroup;
  public routesAlias = routeAlias;
  public expressionBesoinId: string | null = '';
  public expressionBesoinData: any = '';
  activities: ActivtieResponse[] = [];

  public isLoading: boolean = false;
  public loadingActivites: boolean = false;
  public loadingStructures: boolean = false;
  public loadingLignesBudgetaires: boolean = false;

  public structureData: SelectOption[] = [];
  public ligneBudgetaires: SelectOption[] = [];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private activiteService: ActiviteService,
    private AppService: AppService,
    private ligneBudgetaireService: LigneBudgetaireService,
    private expressionBesoinService: ExpressionBesoinService,
    private structureService: StructureService
  ) { }

  ngOnInit(): void {
    this.expressionBesoinId = this.route.snapshot.paramMap.get('id');

    this.expressionBesoinForm = this.fb.group({
      activitesSelectionnees: this.fb.array([]),
      applicationId: this.AppService.getAppId()!,
      complementaryInfo: this.fb.group({
        libelle: [''],
        description: [''],
        supervisor: [''],
        structureResponsable: [''],
        montantEstimatif: [''],
        imputationBudgetaire: [''],
        coutPrevuN1: [''],
        previsionDecaissementN2: [''],
        datePrevision: [''],
        dureePrevisionExecutionMarche: [''],
      })
    });

    if (this.expressionBesoinId) {
      this.loadExpressionBesoinById(this.expressionBesoinId);
    } else {
      console.error("Aucun ID d'activité trouvé dans l'URL.");
    }

    this.activitesFormArray.valueChanges.subscribe(selectedActivityIds => {
      this.updateCommonValues(selectedActivityIds);
    });

    this.loadActivitiesAndExpressionBesoins();
    this.loadLigneBudgetaire();
    this.loadStructures();
  }

  get activitesFormArray(): FormArray {
    return this.expressionBesoinForm.get('activitesSelectionnees') as FormArray;
  }

  get complementaryInfoFormGroup(): FormGroup {
    return this.expressionBesoinForm.get('complementaryInfo') as FormGroup;
  }

  addActivity(): void {
    this.activitesFormArray.push(this.fb.control('', Validators.required));
  }

  removeActivity(index: number): void {
    if (this.activitesFormArray.length > 1) {
      this.activitesFormArray.removeAt(index);
    }
  }

  getAvailableActivities(index: number): ActivtieResponse[] {
    const selectedIds = this.activitesFormArray.value;
    const currentId = this.activitesFormArray.at(index).value;
    return this.activities.filter(activity =>
      !selectedIds.includes(activity.id) || activity.id === currentId
    );
  }

  getAvailableActivitiesAsOptions(index: number): SelectOption[] {
    return this.getAvailableActivities(index).map(a => ({
      value: a.id as string,
      label: a.titre ?? `Activité #${a.id}`
    }));
  }

  loadActivitiesAndExpressionBesoins(): void {
    this.loadingActivites = true;
    this.activiteService.getAllByAppId(this.AppService.getAppId()!).subscribe({
      next: (response1) => {
        const activitiesList = response1.data as ActivtieResponse[];
        this.expressionBesoinService.getAllByAppId(this.AppService.getAppId()!).subscribe({
          next: (response2: any) => {
            const expressionList = response2.data as ActivtieResponse[];
            this.activities = [...activitiesList, ...expressionList];
            this.loadingActivites = false;
          },
          error: (error) => {
            console.error('Erreur lors du chargement des regroupements :', error);
            this.activities = activitiesList;
            this.loadingActivites = false;
          }
        });
      },
      error: (error) => {
        console.error("Erreur lors du chargement des activités", error);
        this.loadingActivites = false;
      }
    });
  }

  loadStructures(): void {
    this.loadingStructures = true;
    this.structureService.getAllByAppId(this.AppService.getAppId()!).subscribe({
      next: (response: any) => {
        this.structureData = response.data.map((item: StructureResponse) => ({
          value: item.code,
          label: item?.code
        }));
        this.loadingStructures = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des structures :', error);
        this.loadingStructures = false;
      }
    });
  }

  loadLigneBudgetaire(): void {
    this.loadingLignesBudgetaires = true;
    this.ligneBudgetaireService.getAllByAppId(this.AppService.getAppId()!).subscribe({
      next: (response: any) => {
        this.ligneBudgetaires = response.data.map((item: LigneBudgetaireResponse) => ({
          value: item.libelle,
          label: item?.libelle
        }));
        this.loadingLignesBudgetaires = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des lignes budgétaires :', error);
        this.loadingLignesBudgetaires = false;
      }
    });
  }

  loadExpressionBesoinById(id: string): void {
    this.expressionBesoinService.getById(id).subscribe({
      next: (response: any) => {
        this.expressionBesoinData = response.data;

        this.activitesFormArray.clear();

        const activityIds: string[] = [
          ...(Array.isArray(response.data.activites) ? response.data.activites : []),
          ...(Array.isArray(response.data.regroupements)
            ? response.data.regroupements.map((item: { id: string }) => item.id)
            : [])
        ];

        activityIds.forEach((activityId: string) => {
          this.activitesFormArray.push(this.fb.control(activityId, Validators.required));
        });

        const patchData = {
          activitesSelectionnees: this.activitesFormArray.value,
          complementaryInfo: {
            libelle: response.data.titre,
            description: response.data.objectif,
          }
        };

        response.data.caracteristiques.forEach((detail: any) => {
          const formControlName = toCamelCase(detail.code);
          (patchData.complementaryInfo as any)[formControlName] = detail.valeur;
        });

        this.expressionBesoinForm.patchValue(patchData);
      },
      error: (error) => {
        console.error("Erreur lors du chargement de l'activité par ID :", error);
      }
    });
  }

  updateCommonValues(selectedActivityIds: string[]): void {
    if (!selectedActivityIds || selectedActivityIds.length === 0) {
      this.resetComplementaryInfo();
      return;
    }
    const selectedActivities = this.activities.filter(activity =>
      selectedActivityIds.includes(activity.id!)
    );
    if (selectedActivities.length === 0) return;
    const commonValues = this.getCommonCharacteristics(selectedActivities);
    this.updateFormWithCommonValues(commonValues);
  }

  getCommonCharacteristics(activities: ActivtieResponse[]): any {
    const commonValues: any = { supervisor: null, structureResponsable: null, imputationBudgetaire: null };
    if (activities.length === 0) return commonValues;

    const supervisors = activities.map(a => this.getCharacteristicValue(a, 'SUPERVISOR'));
    commonValues.supervisor = this.allValuesEqual(supervisors) ? supervisors[0] : '';

    const structures = activities.map(a => this.getCharacteristicValue(a, 'STRUCTURE_RESPONSABLE'));
    commonValues.structureResponsable = this.allValuesEqual(structures) ? structures[0] : '';

    const imputations = activities.map(a => this.getCharacteristicValue(a, 'IMPUTATION_BUDGETAIRE'));
    commonValues.imputationBudgetaire = this.allValuesEqual(imputations) ? imputations[0] : '';

    return commonValues;
  }

  getCharacteristicValue(activity: ActivtieResponse, code: string): string {
    const c = activity.caracteristiques?.find(c => c.code === code);
    return c ? c.valeur : '';
  }

  allValuesEqual(values: string[]): boolean {
    if (values.length === 0) return false;
    const nonEmpty = values.filter(v => v && v.trim() !== '');
    if (nonEmpty.length === 0) return false;
    return nonEmpty.every(v => v === nonEmpty[0]);
  }

  updateFormWithCommonValues(commonValues: any): void {
    const complementaryInfo = this.complementaryInfoFormGroup;
    ['supervisor', 'structureResponsable', 'imputationBudgetaire'].forEach(field => {
      const control = complementaryInfo.get(field);
      if (control && !control.dirty) {
        control.setValue(commonValues[field] || '');
      }
    });
  }

  resetComplementaryInfo(): void {
    this.complementaryInfoFormGroup.patchValue({
      supervisor: '',
      structureResponsable: '',
      imputationBudgetaire: ''
    });
    this.complementaryInfoFormGroup.markAsPristine();
  }

  onSubmit(): void {
    if (this.expressionBesoinForm.valid) {
      const caracteristiquesArray: { code: string; valeur: any }[] = [];
      const excluded = ['libelle', 'description'];
      Object.keys(this.expressionBesoinForm.value.complementaryInfo).forEach(key => {
        if (!excluded.includes(key)) {
          caracteristiquesArray.push({
            code: toSnakeCaseUpper(key),
            valeur: this.expressionBesoinForm.value.complementaryInfo[key] ?? ''
          });
        }
      });

      this.isLoading = true;
      const requestBody: ExpressionBesoinReq = {
        applicationId: this.expressionBesoinForm.value.applicationId,
        activites: this.expressionBesoinForm.value.activitesSelectionnees,
        typeActivite: 'MARCHE',
        titre: this.expressionBesoinForm.value.complementaryInfo.libelle,
        objectif: this.expressionBesoinForm.value.complementaryInfo.description,
        caracteristiques: caracteristiquesArray
      };

      this.expressionBesoinService.update(this.expressionBesoinId!, requestBody).subscribe({
        next: () => {
          this.isLoading = false;
          this.router.navigate([
            routeAlias.marches.home,
            routeAlias.marches.expression_besoin.home,
          ]);
        },
        error: (error) => {
          console.error('Erreur lors de la mise à jour :', error);
          this.isLoading = false;
        }
      });
    } else {
      console.error('Le formulaire est invalide');
      this.expressionBesoinForm.markAllAsTouched();
    }
  }
}